import React, { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { API } from '../config';
import { playTone, requestNotificationPermission } from './nawara/nawaraSound';

const TONE_OPTIONS = [
  { id: 'buzz', label: 'Zumbido corto', emoji: '\u26A1' },
  { id: 'buzz_long', label: 'Zumbido largo', emoji: '\u23F1' },
  { id: 'voice_nawara', label: 'Voz: \u00a1NAWARA!', emoji: '\u{1F50A}' },
];

const RESPONSE_LABEL = {
  going: 'Voy',
  cant: 'No puedo',
  calling: 'Llamando',
};

const EMERGENCY_HOLD_MS = 3000;

const NawaraComponent = () => {
  const [tab, setTab] = useState('new'); // new | inbox | sent
  const [contacts, setContacts] = useState([]);
  const [selected, setSelected] = useState([]); // user_ids
  const [tone, setTone] = useState('buzz');
  const [sending, setSending] = useState(false);
  const [inbox, setInbox] = useState([]);
  const [sent, setSent] = useState([]);
  const [search, setSearch] = useState('');
  const [feedback, setFeedback] = useState(null);
  const [notifPerm, setNotifPerm] = useState(typeof Notification !== 'undefined' ? Notification.permission : 'unsupported');

  // SOS / Emergencia
  const [trusted, setTrusted] = useState([]);
  const [trustedDraft, setTrustedDraft] = useState([]);
  const [trustedSaving, setTrustedSaving] = useState(false);
  const [trustedFeedback, setTrustedFeedback] = useState(null);
  const [holdProgress, setHoldProgress] = useState(0); // 0-100
  const [emergencyFiring, setEmergencyFiring] = useState(false);
  const [lastEmergency, setLastEmergency] = useState(null); // { call_id, sentAt, cancelled }
  const [cancelCountdown, setCancelCountdown] = useState(0); // 30 -> 0 segundos
  const holdTimerRef = useRef(null);
  const holdRafRef = useRef(null);
  const cancelTimerRef = useRef(null);

  const fetchContacts = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/nawara/contacts`);
      setContacts(res.data.contacts || []);
    } catch (e) { /* ignore */ }
  }, []);

  const fetchInbox = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/nawara/inbox`);
      setInbox(res.data.calls || []);
    } catch (e) { /* ignore */ }
  }, []);

  const fetchSent = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/nawara/sent`);
      setSent(res.data.calls || []);
    } catch (e) { /* ignore */ }
  }, []);

  const fetchTrusted = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/nawara/trusted`);
      const list = res.data.trusted || [];
      setTrusted(list);
      setTrustedDraft(list.map(t => t.id));
    } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => {
    fetchContacts();
    fetchInbox();
    fetchSent();
    fetchTrusted();
  }, [fetchContacts, fetchInbox, fetchSent, fetchTrusted]);

  // Refresca inbox cuando llega evento global de NAWARA
  useEffect(() => {
    const onAlert = () => fetchInbox();
    const onResp = () => fetchSent();
    const onCancel = () => { fetchInbox(); fetchSent(); };
    window.addEventListener('cualapp:nawara_alert', onAlert);
    window.addEventListener('cualapp:nawara_response', onResp);
    window.addEventListener('cualapp:nawara_cancel', onCancel);
    return () => {
      window.removeEventListener('cualapp:nawara_alert', onAlert);
      window.removeEventListener('cualapp:nawara_response', onResp);
      window.removeEventListener('cualapp:nawara_cancel', onCancel);
    };
  }, [fetchInbox, fetchSent]);

  const toggleContact = (id) => {
    setSelected(prev => {
      if (prev.includes(id)) return prev.filter(x => x !== id);
      if (prev.length >= 7) return prev; // max 7
      return [...prev, id];
    });
  };

  const sendCall = async () => {
    if (selected.length === 0) {
      setFeedback({ type: 'error', text: 'Elige entre 1 y 7 destinatarios' });
      return;
    }
    setSending(true);
    setFeedback(null);
    try {
      // pre-warm audio context con un toque local muy bajo
      try { playTone(tone); } catch (e) { /* ignore */ }
      const res = await axios.post(`${API}/nawara/call`, {
        recipient_ids: selected,
        tone,
      });
      setFeedback({ type: 'success', text: `NAWARA enviado a ${res.data.delivered_to} contacto(s)` });
      setSelected([]);
      fetchSent();
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Error enviando NAWARA';
      setFeedback({ type: 'error', text: msg });
    } finally {
      setSending(false);
    }
  };

  const respond = async (callId, response) => {
    try {
      await axios.post(`${API}/nawara/${callId}/respond`, { response });
      fetchInbox();
    } catch (e) { /* ignore */ }
  };

  const askPerm = async () => {
    const r = await requestNotificationPermission();
    setNotifPerm(r);
  };

  // ---- SOS / Emergencia ----

  const toggleTrustedDraft = (id) => {
    setTrustedDraft(prev => {
      if (prev.includes(id)) return prev.filter(x => x !== id);
      if (prev.length >= 5) return prev;
      return [...prev, id];
    });
  };

  const saveTrusted = async () => {
    setTrustedSaving(true);
    setTrustedFeedback(null);
    try {
      await axios.put(`${API}/nawara/trusted`, { contact_ids: trustedDraft });
      await fetchTrusted();
      setTrustedFeedback({ type: 'success', text: `Guardados ${trustedDraft.length} contacto(s) de confianza` });
    } catch (e) {
      setTrustedFeedback({ type: 'error', text: e?.response?.data?.detail || 'Error guardando' });
    } finally {
      setTrustedSaving(false);
    }
  };

  const getGeolocation = () => new Promise((resolve) => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) return resolve(null);
    let resolved = false;
    const done = (val) => { if (!resolved) { resolved = true; resolve(val); } };
    navigator.geolocation.getCurrentPosition(
      (pos) => done({
        lat: pos.coords.latitude,
        lng: pos.coords.longitude,
        accuracy_m: pos.coords.accuracy,
      }),
      () => done(null),
      { enableHighAccuracy: true, timeout: 5000, maximumAge: 30000 }
    );
    // Hard timeout
    setTimeout(() => done(null), 5500);
  });

  const fireEmergency = async () => {
    if (emergencyFiring) return;
    setEmergencyFiring(true);
    setTrustedFeedback(null);
    try {
      const loc = await getGeolocation();
      const body = {};
      if (loc) {
        body.lat = loc.lat;
        body.lng = loc.lng;
        body.accuracy_m = loc.accuracy_m;
      }
      // Pre-warm + tono local urgente
      try { playTone('emergency'); } catch (e) { /* ignore */ }
      const res = await axios.post(`${API}/nawara/emergency`, body);
      setTrustedFeedback({
        type: 'success',
        text: `\u{1F198} EMERGENCIA enviada a ${res.data.delivered_to} contacto(s) de confianza${res.data.location ? ' con ubicacion GPS' : ''}`,
      });
      // Activar ventana de cancelacion (30s)
      setLastEmergency({ call_id: res.data.call_id, sentAt: Date.now(), cancelled: false });
      setCancelCountdown(30);
      if (cancelTimerRef.current) clearInterval(cancelTimerRef.current);
      cancelTimerRef.current = setInterval(() => {
        setCancelCountdown(prev => {
          if (prev <= 1) {
            clearInterval(cancelTimerRef.current);
            cancelTimerRef.current = null;
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      fetchSent();
    } catch (e) {
      setTrustedFeedback({ type: 'error', text: e?.response?.data?.detail || 'Error enviando emergencia' });
    } finally {
      setEmergencyFiring(false);
    }
  };

  const cancelLastEmergency = async () => {
    if (!lastEmergency?.call_id || lastEmergency.cancelled) return;
    try {
      const res = await axios.post(`${API}/nawara/${lastEmergency.call_id}/cancel`);
      setLastEmergency(prev => prev ? { ...prev, cancelled: true } : null);
      setCancelCountdown(0);
      if (cancelTimerRef.current) {
        clearInterval(cancelTimerRef.current);
        cancelTimerRef.current = null;
      }
      setTrustedFeedback({
        type: 'success',
        text: `Falsa alarma. ${res.data.notified_count} contacto(s) notificado(s) que estas bien.`,
      });
      fetchSent();
    } catch (e) {
      setTrustedFeedback({ type: 'error', text: e?.response?.data?.detail || 'Error cancelando' });
    }
  };

  // Cleanup intervalos al desmontar
  useEffect(() => {
    return () => {
      if (cancelTimerRef.current) clearInterval(cancelTimerRef.current);
      if (holdTimerRef.current) clearTimeout(holdTimerRef.current);
      if (holdRafRef.current) cancelAnimationFrame(holdRafRef.current);
    };
  }, []);

  const startHold = () => {
    if (trusted.length === 0) {
      setTrustedFeedback({ type: 'error', text: 'Configura al menos 1 contacto de confianza primero' });
      return;
    }
    if (emergencyFiring) return;
    const startedAt = Date.now();
    const tick = () => {
      const elapsed = Date.now() - startedAt;
      const pct = Math.min(100, (elapsed / EMERGENCY_HOLD_MS) * 100);
      setHoldProgress(pct);
      if (pct < 100) {
        holdRafRef.current = requestAnimationFrame(tick);
      }
    };
    holdRafRef.current = requestAnimationFrame(tick);
    holdTimerRef.current = setTimeout(() => {
      setHoldProgress(100);
      fireEmergency();
      // reset progress after a second
      setTimeout(() => setHoldProgress(0), 800);
    }, EMERGENCY_HOLD_MS);
  };

  const cancelHold = () => {
    if (holdTimerRef.current) {
      clearTimeout(holdTimerRef.current);
      holdTimerRef.current = null;
    }
    if (holdRafRef.current) {
      cancelAnimationFrame(holdRafRef.current);
      holdRafRef.current = null;
    }
    setHoldProgress(0);
  };

  const filteredContacts = contacts.filter(c => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (c.full_name || '').toLowerCase().includes(s) || (c.email || '').toLowerCase().includes(s);
  });

  return (
    <div className="bg-white rounded-2xl shadow-sm p-6" data-testid="nawara-component">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900" data-testid="nawara-title">NAWARA</h2>
          <p className="text-xs text-gray-500 mt-0.5">{'Toque directo a 1\u20137 contactos.'}</p>
        </div>
        {notifPerm !== 'granted' && notifPerm !== 'unsupported' && (
          <button onClick={askPerm} data-testid="nawara-permission-btn"
            className="text-xs px-3 py-1.5 rounded-full bg-amber-100 text-amber-800 hover:bg-amber-200">
            Activar avisos del navegador
          </button>
        )}
      </div>

      <div className="flex gap-2 mb-5 border-b border-gray-100">
        {[
          { id: 'new', label: 'Nuevo' },
          { id: 'inbox', label: `Recibidos${inbox.length ? ` (${inbox.length})` : ''}` },
          { id: 'sent', label: 'Enviados' },
          { id: 'sos', label: 'SOS' },
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            data-testid={`nawara-tab-${t.id}`}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
              tab === t.id ? 'border-red-600 text-red-700' : 'border-transparent text-gray-500 hover:text-gray-800'
            }`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'new' && (
        <div className="space-y-5">
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">Tono</label>
            <div className="flex flex-wrap gap-2">
              {TONE_OPTIONS.map(opt => (
                <button key={opt.id} onClick={() => { setTone(opt.id); playTone(opt.id); }}
                  data-testid={`nawara-tone-${opt.id}`}
                  className={`px-3 py-1.5 rounded-full text-sm border ${
                    tone === opt.id ? 'bg-red-600 text-white border-red-600' : 'bg-white text-gray-700 border-gray-300 hover:border-red-400'
                  }`}>
                  <span className="mr-1">{opt.emoji}</span>{opt.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">
              Destinatarios ({selected.length}/7)
            </label>
            <input type="text" value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Buscar contactos..."
              data-testid="nawara-search-contacts"
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm mb-2" />
            <div className="max-h-72 overflow-y-auto rounded-lg border border-gray-100 divide-y divide-gray-50">
              {filteredContacts.length === 0 ? (
                <div className="p-4 text-center text-sm text-gray-400">Sin contactos disponibles.</div>
              ) : filteredContacts.map(c => {
                const checked = selected.includes(c.id);
                const disabled = !checked && selected.length >= 7;
                return (
                  <button key={c.id} onClick={() => toggleContact(c.id)} disabled={disabled}
                    data-testid={`nawara-contact-${c.id}`}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 text-left ${
                      checked ? 'bg-red-50' : disabled ? 'opacity-40' : 'hover:bg-gray-50'
                    }`}>
                    <span className={`w-4 h-4 rounded border flex items-center justify-center ${
                      checked ? 'bg-red-600 border-red-600' : 'border-gray-300'
                    }`}>
                      {checked && <span className="text-white text-[10px]">{'\u2713'}</span>}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-900 truncate">{c.full_name || c.email}</div>
                      <div className="text-xs text-gray-500 truncate">{c.email}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {feedback && (
            <div data-testid="nawara-feedback" className={`text-sm rounded-lg px-3 py-2 ${
              feedback.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-700'
            }`}>{feedback.text}</div>
          )}

          <button onClick={sendCall} disabled={sending || selected.length === 0}
            data-testid="nawara-send-btn"
            className="w-full py-3 rounded-xl bg-red-600 text-white text-base font-bold hover:bg-red-700 disabled:bg-gray-300">
            {sending ? 'Enviando...' : `\u26A1 NAWARA (${selected.length})`}
          </button>
        </div>
      )}

      {tab === 'inbox' && (
        <div className="space-y-3" data-testid="nawara-inbox">
          {inbox.length === 0 ? (
            <div className="p-6 text-center text-sm text-gray-400">Sin llamados recibidos.</div>
          ) : inbox.map(c => (
            <div key={c.id} data-testid={`nawara-inbox-item-${c.id}`}
              className={`p-4 rounded-xl border ${
                c.cancelled
                  ? 'border-green-300 bg-green-50 opacity-80'
                  : c.emergency
                  ? 'border-red-300 bg-gradient-to-r from-red-100 to-orange-50 shadow-md'
                  : 'border-gray-100 bg-gradient-to-r from-red-50/50 to-white'
              }`}>
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-sm font-semibold text-gray-900">
                    {c.cancelled ? <span className="mr-1">{'\u2713'}</span>
                      : c.emergency ? <span className="mr-1">{'\u{1F198}'}</span> : null}
                    {c.caller_name}
                  </div>
                  <div className="text-xs text-gray-500">
                    {new Date(c.created_at).toLocaleString('es-VE')} · tono: {c.tone}
                  </div>
                  {c.cancelled && (
                    <div className="mt-1 text-xs text-green-800 font-medium">
                      Falsa alarma - {c.caller_name} esta bien
                    </div>
                  )}
                  {!c.cancelled && c.emergency && c.location && (
                    <a
                      href={`https://www.google.com/maps?q=${c.location.lat},${c.location.lng}`}
                      target="_blank" rel="noopener noreferrer"
                      data-testid={`nawara-inbox-loc-${c.id}`}
                      className="inline-block mt-1 text-xs text-blue-600 underline">
                      {`Ver ubicacion en mapa (${c.location.lat.toFixed(4)}, ${c.location.lng.toFixed(4)})`}
                    </a>
                  )}
                  {!c.cancelled && c.note && <div className="text-xs text-gray-700 mt-1">"{c.note}"</div>}
                </div>
                <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${
                  c.cancelled
                    ? 'bg-green-200 text-green-800'
                    : c.emergency ? 'bg-red-600 text-white animate-pulse' : 'bg-red-100 text-red-700'
                }`}>{c.cancelled ? 'FALSA ALARMA' : c.emergency ? 'SOS' : 'NAWARA'}</span>
              </div>
              {!c.cancelled && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {['going', 'cant', 'calling'].map(r => (
                    <button key={r} onClick={() => respond(c.id, r)}
                      data-testid={`nawara-respond-${c.id}-${r}`}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium border ${
                        c.my_response?.response === r
                          ? 'bg-red-600 text-white border-red-600'
                          : 'bg-white text-gray-700 border-gray-300 hover:border-red-400'
                      }`}>
                      {RESPONSE_LABEL[r]}
                    </button>
                  ))}
                  {c.my_response && (
                    <span className="text-xs text-gray-500 self-center">Tu respuesta: {RESPONSE_LABEL[c.my_response.response]}</span>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {tab === 'sent' && (
        <div className="space-y-3" data-testid="nawara-sent">
          {sent.length === 0 ? (
            <div className="p-6 text-center text-sm text-gray-400">Aun no enviaste llamados.</div>
          ) : sent.map(c => (
            <div key={c.id} data-testid={`nawara-sent-item-${c.id}`}
              className="p-4 rounded-xl border border-gray-100">
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-sm font-semibold text-gray-900">{c.recipient_ids.length} destinatario(s)</div>
                  <div className="text-xs text-gray-500">
                    {new Date(c.created_at).toLocaleString('es-VE')} · tono: {c.tone}
                  </div>
                </div>
              </div>
              {c.responses && Object.keys(c.responses).length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {Object.entries(c.responses).map(([uid, r]) => (
                    <span key={uid} className="text-[11px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-700">
                      {r.name || uid.slice(0, 6)}: {RESPONSE_LABEL[r.response] || r.response}
                    </span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      {tab === 'sos' && (
        <div className="space-y-6" data-testid="nawara-sos">
          {/* Boton de emergencia */}
          <div className="rounded-2xl border-2 border-red-200 bg-gradient-to-br from-red-50 via-orange-50 to-yellow-50 p-5">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xl">{'\u{1F198}'}</span>
              <h3 className="text-base font-bold text-red-800">Zumbido de Emergencia</h3>
            </div>
            <p className="text-xs text-gray-700 mb-4">
              Manten presionado el boton 3 segundos. Se enviara un NAWARA simultaneo a todos
              tus contactos de confianza con tu ubicacion GPS.
            </p>

            <div className="flex flex-col items-center py-2">
              <button
                onMouseDown={startHold}
                onMouseUp={cancelHold}
                onMouseLeave={cancelHold}
                onTouchStart={startHold}
                onTouchEnd={cancelHold}
                onTouchCancel={cancelHold}
                onContextMenu={(e) => e.preventDefault()}
                disabled={emergencyFiring || trusted.length === 0}
                data-testid="nawara-sos-button"
                className={`relative w-40 h-40 rounded-full font-extrabold text-white text-lg select-none touch-none shadow-2xl transition-transform ${
                  emergencyFiring
                    ? 'bg-red-700 scale-95'
                    : trusted.length === 0
                    ? 'bg-gray-300'
                    : 'bg-gradient-to-br from-red-500 to-red-700 active:scale-95 hover:from-red-600 hover:to-red-800'
                }`}>
                <span className="relative z-10">
                  {emergencyFiring ? 'ENVIANDO...' : holdProgress > 0 ? `${Math.round(holdProgress)}%` : 'SOS'}
                </span>
                {/* Anillo de progreso */}
                {holdProgress > 0 && (
                  <svg className="absolute inset-0 w-full h-full -rotate-90 pointer-events-none" viewBox="0 0 100 100">
                    <circle cx="50" cy="50" r="46" fill="none" stroke="rgba(255,255,255,0.3)" strokeWidth="4" />
                    <circle
                      cx="50" cy="50" r="46" fill="none" stroke="white" strokeWidth="4" strokeLinecap="round"
                      strokeDasharray={`${(holdProgress / 100) * 289} 289`}
                    />
                  </svg>
                )}
              </button>
              <p className="mt-3 text-xs text-gray-600">
                {trusted.length === 0
                  ? 'Configura primero tus contactos de confianza abajo.'
                  : `Manten presionado 3 seg. para enviar a ${trusted.length} contacto(s).`}
              </p>
            </div>

            {/* Banner cancelar SOS (visible 30 seg. tras emergency) */}
            {lastEmergency && !lastEmergency.cancelled && cancelCountdown > 0 && (
              <div data-testid="nawara-cancel-banner"
                className="mt-4 rounded-xl border-2 border-amber-400 bg-amber-50 p-3 flex items-center gap-3">
                <div className="flex-1">
                  <div className="text-sm font-semibold text-amber-900">
                    {'\u26A0'} Tienes {cancelCountdown}s para cancelar (falsa alarma)
                  </div>
                  <div className="text-xs text-amber-800 mt-0.5">
                    Si fue por error, cancela ahora. Tus contactos seran notificados que estas bien.
                  </div>
                </div>
                <button onClick={cancelLastEmergency}
                  data-testid="nawara-cancel-sos-btn"
                  className="px-4 py-2 rounded-lg bg-amber-600 text-white text-sm font-bold hover:bg-amber-700 whitespace-nowrap">
                  Cancelar SOS
                </button>
              </div>
            )}

            {lastEmergency?.cancelled && (
              <div data-testid="nawara-cancelled-confirmation"
                className="mt-4 rounded-xl border border-green-300 bg-green-50 p-3 text-sm text-green-800">
                {'\u2713'} SOS cancelado. Tus contactos saben que estas bien.
              </div>
            )}

            {trustedFeedback && (
              <div data-testid="nawara-sos-feedback" className={`mt-3 text-sm rounded-lg px-3 py-2 ${
                trustedFeedback.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-700'
              }`}>{trustedFeedback.text}</div>
            )}
          </div>

          {/* Configuracion de contactos de confianza */}
          <div className="rounded-2xl border border-gray-200 p-5">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-base font-bold text-gray-900">Contactos de confianza</h3>
              <span className="text-xs text-gray-500">{trustedDraft.length}/5</span>
            </div>
            <p className="text-xs text-gray-600 mb-3">
              Hasta 5 contactos. Reciben tu llamado de emergencia con un solo gesto.
            </p>

            {trusted.length > 0 && (
              <div className="mb-3 flex flex-wrap gap-1.5">
                {trusted.map(t => (
                  <span key={t.id} data-testid={`nawara-trusted-active-${t.id}`}
                    className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700 font-medium">
                    {t.full_name || t.email}
                  </span>
                ))}
              </div>
            )}

            <div className="max-h-60 overflow-y-auto rounded-lg border border-gray-100 divide-y divide-gray-50 mb-3">
              {contacts.length === 0 ? (
                <div className="p-3 text-center text-sm text-gray-400">Sin contactos disponibles.</div>
              ) : contacts.map(c => {
                const checked = trustedDraft.includes(c.id);
                const disabled = !checked && trustedDraft.length >= 5;
                return (
                  <button key={c.id} onClick={() => toggleTrustedDraft(c.id)} disabled={disabled}
                    data-testid={`nawara-trusted-toggle-${c.id}`}
                    className={`w-full flex items-center gap-3 px-3 py-2 text-left ${
                      checked ? 'bg-red-50' : disabled ? 'opacity-40' : 'hover:bg-gray-50'
                    }`}>
                    <span className={`w-4 h-4 rounded border flex items-center justify-center ${
                      checked ? 'bg-red-600 border-red-600' : 'border-gray-300'
                    }`}>
                      {checked && <span className="text-white text-[10px]">{'\u2713'}</span>}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-900 truncate">{c.full_name || c.email}</div>
                      <div className="text-xs text-gray-500 truncate">{c.email}</div>
                    </div>
                  </button>
                );
              })}
            </div>

            <button onClick={saveTrusted} disabled={trustedSaving}
              data-testid="nawara-trusted-save"
              className="w-full py-2.5 rounded-lg bg-gray-900 text-white text-sm font-semibold hover:bg-black disabled:bg-gray-400">
              {trustedSaving ? 'Guardando...' : 'Guardar contactos de confianza'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default NawaraComponent;
